/**
 * Creates a new DOM element with the supplied attributes.
 * @param {String} type The type of element to create. E.g. 'div', 'input'..
 * @param {Object} attributes An object with the attrbutes to attach to the element.
 * @return {!HTMLElement} A new DOM element with the supplied attributes.
 */
function createElement(type, attributes) {
    const element = document.createElement(type);
    if (!attributes) { return element; }

    const properties = Object.keys(attributes);

    for (let i = 0; i < properties.length; i += 1) {
        element.setAttribute(properties[i], attributes[properties[i]]);
    }

    return element;
}


/**
 * Removes all the children attached to the element.
 * @param {HTMLElement} element The element whose children will be removed.
 * @return {!HTMLElement} The original element with its children removed.
 */
function clearChildren(element) {
    while (element.firstChild) element.removeChild(element.firstChild);
    return element;
}

/**
 * Gets the attributes of an element as an  object.
 * @param {Element} element The element whose attributes to get.
 * @return {!Object} An object whose keys are the attribute keys and whose
 *     values are the attribute values. If no attributes exist, an empty
 *     object is returned.
 */
function getAttributes(element) {
    const attrs = {};

    // Validate input. nodeType 1 is Node.ELEMENT_NODE.
    if (!(element && element.nodeType === 1)) return attrs;

    // Return an empty object if there are no attributes.
    const map = element.attributes;
    if (map.length === 0) return {};

    for (let i = 0; i < map.length; i += 1) {
        const attribute = map[i];
        attrs[attribute.name] = attribute.value;
    }
    return attrs;
}

/**
 * Method that checks whether cls is present in element object.
 * @param  {Object} element DOM element which needs to be checked
 * @param  {Object} _class Classname is tested
 * @return {Boolean} True if cls is present, false otherwise.
 */
function hasClass(element, _class) {
    return element.getAttribute('class').indexOf(_class) > -1;
}


/**
 * Takes an element and removes all instances of a class from the element
 * and all of its children.
 * @param {HTMLElement} parent The parent and its children who will have class removed.
 * @param {String} classToRemove The class which will be removed. No '.' should be used.
 * @return {!HTMLElement} The original parent, now with the specified class removed.
 */
function clearClass(parent, classToRemove) {
    const elements = parent.getElementsByClassName(classToRemove);

    for (let i = 0; i < elements.length; i += 1) {
        const element = elements[i];
        element.classList.remove(classToRemove);
    }

    return parent;
}


/**
 * Takes an element and removes all instances of a class from the element
 * and all of its children.
 * @param {HTMLElement} parent The parent and its children who will have class removed.
 * @param {String} classToRemove The class which will be removed. No '.' should be used.
 * @return {!HTMLElement} The original parent, now with the specified class removed.
 */
function clearClassQ(parent, classToRemove) {
    const elements = parent.querySelectorAll(`.${classToRemove}`);

    for (let i = 0; i < elements.length; i += 1) {
        const element = elements[i];
        element.classList.remove(classToRemove);
    }

    return parent;
}

/**
 * Takes a URL, creates a GET requests and returns the data in a callback.
 * It is possible to include context with request.
 * @param {String} url The URL which will be requested.
 * @param {Function} callback The function which will be called when the request is completed.
 * The callback will have the format (false, response, context) if succeeeded.
 * The callback will have the format (true, error, context) if failed or aborted.
 * @param {Any} context A context which will be return when the call is completed.
 * @return {!HTMLElement} The XMLHttpRequest, with all its prototypes. Meaning that it is abortable.
 */
function get(url, callback, context) {
    const xhr = new XMLHttpRequest();

    xhr.onreadystatechange = function onreadystatechange() {
        if (this.readyState === 4 && this.status === 200) {
            callback(false, this.responseText, context);
        }
    };

    xhr.onerror = function onerror(err) { callback(err, null, context); };
    xhr.onabort = function onabort(msg) { callback(msg, null, context); };

    xhr.open('GET', url, true);

    // xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

    xhr.send();

    return xhr;
}

function createGeojsonPoint(properties, coordinates) {
    return {
        type: 'Feature',
        properties,
        geometry: {
            type: 'Point',
            coordinates,
        },
    };
}

function setCookie(n, v, h) {
    const d = new Date();
    d.setTime(d.getTime() + (h * 60 * 60 * 1000));
    const e = `expires=${d.toUTCString()}`;
    document.cookie = `${n}=${v};${e};path=/`;
}

function getCookie(cname) {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i += 1) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return '';
}

/* eslint-disable */
function getQueryString() {
    const res = {};
    let key = false;
    let itm = null;
    // get the query string without the ?
    const qs = location.search.substring(1);
    // check for the key as an argument
    if (arguments.length > 0 && arguments[0].length > 1) { key = arguments[0]; }
    // make a regex pattern to grab key/value
    const pattern = /([^&=]+)=([^&]*)/g;
    // loop the items in the query string, either
    // find a match to the argument, or build an object
    // with key/value pairs
    while (itm = pattern.exec(qs)) {
        if (key !== false && decodeURIComponent(itm[1]) === key) {
            return decodeURIComponent(itm[2]);
        } else if (key === false) {
            res[decodeURIComponent(itm[1])] = decodeURIComponent(itm[2]);
        }
    }

    return key === false ? res : null;
}
/* eslint-enable */

export {
    createElement,
    createGeojsonPoint,
    clearChildren,
    clearClass,
    clearClassQ,
    getAttributes,
    get,
    getQueryString,
    getCookie,
    setCookie,
    hasClass,
};
